import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { ModalHead, ModalBody, Hint, TableWrap, Tbl, Empty, Chip, SegButtons, Who } from './ZetaUi';
import ZetaMemberModal from './ZetaMemberModal';
import { groupFaceFails, faceFailReasonText, fromUnix, md, clock } from './zetaUtil';

// 얼굴로그인 실패 기록 — 같은 사람의 반복 실패를 한 줄로 합쳐 본다(규칙은 zetaUtil.groupFaceFails).
//   데이터는 대시보드가 고른 기간으로 이미 불러온 facefaillog 그대로다(최대 500건).

// 사진 = 러닝앱 카메라 화면 전체(640×480, 4:3). 얼굴을 알아볼 수 있게 크게 — 사람별 행(여러 장)은 중간,
//   건별 행(한 장)은 크게. contain 이라 비율이 다른 사진도 잘리지 않는다. 누르면 원본을 새 탭으로 연다.
const Thumbs = styled.span`
  display: inline-flex;
  gap: 6px;
  align-items: center;
  img { display: block; width: 128px; height: 96px; object-fit: contain; background: #f1f3f5; border-radius: 6px; border: 1px solid #e0e4ea; }
  &[data-size='lg'] img { width: 224px; height: 168px; }
`;
const Reasons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  span { padding: 2px 8px; border-radius: 12px; background: #f8f9fb; border: 1px solid #e0e4ea; font-size: 12px; color: #495057; }
`;

const photos = (rows, max = 3, size = 'md') => {
  const urls = rows.map((f) => f.imageURL).filter(Boolean);
  if (!urls.length) return <span style={{ color: '#868e96' }}>사진 없음</span>;
  return (
    <Thumbs data-size={size}>
      {urls.slice(0, max).map((u) => <a key={u} href={u} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}><img src={u} alt="실패 순간 사진" /></a>)}
      {urls.length > max ? <span style={{ fontSize: 12, color: '#868e96' }}>+{urls.length - max}</span> : null}
    </Thumbs>
  );
};
const when = (p) => {
  const a = fromUnix(p.first.dateTime), b = fromUnix(p.last.dateTime);
  if (md(a) === md(b)) return `${md(a)} ${clock(a)}${p.rows.length > 1 ? ` ~ ${clock(b)}` : ''}`;
  return `${md(a)} ~ ${md(b)}`;
};
const bestText = (f) => (f && f.bestMemberID > 0 ? `${f.bestMemberName || '이름 없음'} (${Number(f.bestScore).toFixed(2)})` : '-');

// 사람별 행의 "뒤이어 번호로 로그인" — 펼치지 않아도 보이게 그 사람의 번호 로그인을 최근 것부터 나열한다.
//   묶음 하나당 번호 로그인은 많아야 한 번(그 실패로 묶음이 끝난다). 여러 날에 걸치면 날짜도 붙인다.
const PHONE_MAX = 3;
const phoneLogins = (p) => {
  const rows = p.rows.filter((f) => f.phoneMemberID > 0);
  if (!rows.length) return <span style={{ color: '#868e96' }}>—</span>;
  const multiDay = md(fromUnix(p.first.dateTime)) !== md(fromUnix(p.last.dateTime));
  return (
    <>
      {rows.slice(0, PHONE_MAX).map((f) => {
        const t = f.phoneLoginTime ? fromUnix(f.phoneLoginTime) : null;
        const day = t && (multiDay || md(t) !== md(fromUnix(f.dateTime))) ? `${md(t)} ` : '';
        return <div key={f.failKey}>{f.phoneMemberName || '이름 없음'}{t ? ` · ${day}${clock(t)}` : ''}</div>;
      })}
      {rows.length > PHONE_MAX ? <div style={{ fontSize: 12, color: '#868e96' }}>외 {rows.length - PHONE_MAX}번</div> : null}
    </>
  );
};

const ZetaFaceFailModal = ({ open, onClose, list, rangeText, clubID, socketKey }) => {
  const [view, setView] = useState('person');
  const [exp, setExp] = useState({});
  const [memberID, setMemberID] = useState(null);
  const G = useMemo(() => groupFaceFails(list || []), [list]);
  const identified = G.persons.filter((p) => p.memberID).length;
  const repeat = G.persons.filter((p) => p.rows.length >= 3).length;
  const gOf = new Map();
  G.groups.forEach((g) => g.rows.forEach((f) => gOf.set(f, g)));

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="xl" fullWidth>
      <ModalHead>
        <div>
          <h3>얼굴로그인 실패 기록</h3>
          <div className="meta"><span>{rangeText}</span></div>
        </div>
        <IconButton onClick={onClose} size="small" aria-label="닫기"><CloseIcon /></IconButton>
      </ModalHead>
      <ModalBody>
        {list === null ? <Empty>실패 기록을 불러오지 못했습니다 (관리자 로그인·클럽 권한을 확인해 주세요)</Empty> : (
          <>
            <Hint>
              같은 기기에서 3분 안에 이어진 실패는 한 사람의 연속 시도로 봅니다(번호로 로그인하면 묶음이 끝남). 러닝앱은 번호 로그인 회원을 마지막 실패에만 붙이므로, 같은 묶음의 앞선 실패도 그 회원으로 봅니다.
              번호 로그인으로 확인된 회원이 같으면 다른 시간·기기의 실패도 한 사람으로 합칩니다. 번호 로그인 없이 떠난 사람은 묶음별로 "확인 안 됨"으로 둡니다.
            </Hint>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 14, color: '#2c3e50' }}>
                실패 <b>{list.length}건</b> → <b>{G.persons.length}명</b> (번호 로그인으로 확인 {identified}명 · 확인 안 됨 {G.persons.length - identified}명) · 3번 이상 실패 <b style={{ color: '#e74c3c' }}>{repeat}명</b>
                {list.length >= 500 ? <span style={{ color: '#e8890c' }}> · 500건까지만 불러왔습니다</span> : null}
              </span>
              <SegButtons options={[['person', '사람별로 합쳐 보기'], ['each', '건별로 보기']]} value={view} onChange={setView} />
            </div>
            <TableWrap>
              {view === 'person' ? (
                <Tbl>
                  <thead><tr><th /><th>사람</th><th className="r">실패</th><th className="r">시도 묶음</th><th>기기</th><th>시각</th><th>원인</th><th>가장 비슷한 회원</th><th>뒤이어 번호로 로그인</th><th>사진</th></tr></thead>
                  <tbody>
                    {G.persons.map((p) => {
                      const isOpen = !!exp[p.key];
                      return (
                        <React.Fragment key={p.key}>
                          <tr className="click" aria-expanded={isOpen} onClick={() => setExp({ ...exp, [p.key]: !isOpen })}>
                            <td style={{ color: '#868e96' }}>{isOpen ? '▾' : '▸'}</td>
                            <td>
                              {p.memberID ? (
                                <span role="button" tabIndex={0} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setMemberID(p.memberID); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setMemberID(p.memberID); } }} title="회원 상세 보기">
                                  <Who name={p.memberName} main={p.memberName || '이름 없음'} sub={`회원번호 ${p.memberID}`} />
                                </span>
                              ) : <><b>확인 안 됨</b><div style={{ fontSize: 12, color: '#868e96' }}>번호 로그인 안 하고 떠남</div></>}
                              {p.memberID && p.rows.length >= 3 ? <div style={{ marginTop: 4 }}><Chip tone="red">얼굴 재등록 권장</Chip></div> : null}
                            </td>
                            <td className="r strong" style={{ color: p.rows.length >= 3 ? '#e74c3c' : undefined }}>{p.rows.length}회</td>
                            <td className="r">{p.groups.length}번</td>
                            <td>{p.machines.length ? `${p.machines.join('·')}번` : '-'}</td>
                            <td>{when(p)}</td>
                            <td><Reasons>{p.reasons.map(([r, n]) => <span key={r}>{faceFailReasonText(r)}{n > 1 ? ` ${n}` : ''}</span>)}</Reasons></td>
                            <td>{bestText(p.best)}</td>
                            <td>{phoneLogins(p)}</td>
                            <td>{photos(p.rows)}</td>
                          </tr>
                          {isOpen ? (
                            <tr className="detail">
                              <td colSpan={10}>
                                <TableWrap>
                                  <Tbl>
                                    <thead><tr><th>시각</th><th>기기</th><th>원인</th><th>가장 비슷한 회원 (점수)</th><th>뒤이어 번호로 로그인</th><th>사진</th></tr></thead>
                                    <tbody>
                                      {p.rows.map((f) => (
                                        <tr key={f.failKey}>
                                          <td>{md(fromUnix(f.dateTime))} {clock(fromUnix(f.dateTime))}</td>
                                          <td>{f.machineNumber ? `${f.machineNumber}번` : ''}</td>
                                          <td>{faceFailReasonText(f.reason)}</td>
                                          <td>{bestText(f)}</td>
                                          <td>{f.phoneMemberID > 0 ? `${f.phoneMemberName || '이름 없음'} · ${clock(fromUnix(f.phoneLoginTime))}` : p.memberID ? <span style={{ color: '#868e96' }}>같은 묶음 → {p.memberName}(으)로 봄</span> : '—'}</td>
                                          <td>{photos([f], 1, 'lg')}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Tbl>
                                </TableWrap>
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Tbl>
              ) : (
                <Tbl>
                  <thead><tr><th>시각</th><th>기기</th><th>원인</th><th>가장 비슷한 회원 (점수)</th><th>뒤이어 번호로 로그인</th><th>합친 사람</th><th>사진</th></tr></thead>
                  <tbody>
                    {list.slice().sort((a, b) => b.dateTime - a.dateTime).map((f) => {
                      const g = gOf.get(f);
                      const p = g && g.person;
                      return (
                        <tr key={f.failKey}>
                          <td>{md(fromUnix(f.dateTime))} {clock(fromUnix(f.dateTime))}</td>
                          <td>{f.machineNumber ? `${f.machineNumber}번` : ''}</td>
                          <td>{faceFailReasonText(f.reason)}</td>
                          <td>{bestText(f)}</td>
                          <td>{f.phoneMemberID > 0 ? `${f.phoneMemberName || '이름 없음'} · ${clock(fromUnix(f.phoneLoginTime))}` : '—'}</td>
                          <td>{p && p.memberID ? `${p.memberName || '이름 없음'} (${p.rows.length}회 중)` : <span style={{ color: '#868e96' }}>확인 안 됨</span>}</td>
                          <td>{photos([f], 1, 'lg')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Tbl>
              )}
              {!list.length ? <Empty>이 기간에 얼굴로그인 실패가 없습니다</Empty> : null}
            </TableWrap>
          </>
        )}
      </ModalBody>
      <ZetaMemberModal open={!!memberID} memberID={memberID} clubID={clubID} socketKey={socketKey} onClose={() => setMemberID(null)} />
    </Dialog>
  );
};

export default ZetaFaceFailModal;
